/**
 * Which messages the chat draws, how one that is not the agent's reply is drawn inside a run, and
 * the text reader that `Messages.tsx` shares with it.
 *
 * Kept JSX-free so `node --test` can load it directly, like `messageRuns.ts`.
 */

import type { Message } from "../types";

/** What a message's content says, dropping its image blocks. */
export function textOf(content: unknown): string {
	if (typeof content === "string") return content;
	if (!Array.isArray(content)) return "";
	return content
		.filter((c): c is { type: "text"; text: string } => (c as { type?: string })?.type === "text")
		.map((c) => c.text)
		.join("");
}

/** A line of status the reader sees, or nothing at all. */
export type Notice = { kind: "note"; text: string } | { kind: "hidden" };

/**
 * A message that is neither a person's, the agent's reply nor a tool result.
 *
 * pi lets a package send a message of its own (`role: "custom"`) — pi-web-access reports a
 * finished background fetch that way, pi-subagents a finished sub-agent. `display` says whether
 * the reader is meant to see it at all and `content` is the whole of what they see; every other
 * field is plumbing. Any other role is the runtime's own — a system prompt, a summary, a role
 * added later — and is never drawn.
 */
export function noticeFor(message: Message): Notice {
	if (message.role !== "custom" || message.display !== true) return { kind: "hidden" };
	const text = textOf(message.content);
	return text === "" ? { kind: "hidden" } : { kind: "note", text };
}

/** Whether the chat draws anything for a message. */
export function isShown(message: Message): boolean {
	return message.role === "user" || message.role === "assistant" || message.role === "toolResult" || noticeFor(message).kind === "note";
}
